import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  MessageCircle, 
  Heart, 
  Trash2, 
  Reply, 
  Send, 
  Loader2,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const MAX_COMMENT_LENGTH = 2000;

const CampaignComments = ({ campaignId, creatorId }) => {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [deleteCommentId, setDeleteCommentId] = useState(null);
  const [likingComment, setLikingComment] = useState(null);

  const isCreator = user && user.id === creatorId;

  // Fetch comments
  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/comments/campaign/${campaignId}`);
      setComments(response.data.comments || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campaignId) {
      fetchComments();
    }
  }, [campaignId]);

  // Post new comment
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated) return;

    setSubmitting(true);
    try {
      const response = await apiClient.post('/comments/', {
        campaign_id: campaignId,
        content: newComment.trim(),
        is_question: false
      });
      
      // Add new comment to the list
      setComments([{ ...response.data, replies: [] }, ...comments]);
      setNewComment('');
      toast.success('Comment posted successfully!');
    } catch (err) {
      console.error('Error posting comment:', err);
      toast.error(err.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  // Reply to comment (creator only)
  const handleReply = async (commentId) => {
    if (!replyContent.trim() || !isCreator) return;

    setSubmitting(true);
    try {
      const response = await apiClient.post(`/comments/${commentId}/reply`, {
        content: replyContent.trim()
      });
      
      // Update the comment with new reply
      setComments(comments.map(c => {
        if (c.id === commentId) {
          return {
            ...c,
            replies: [...(c.replies || []), response.data.reply]
          };
        }
        return c;
      }));
      
      setReplyingTo(null);
      setReplyContent('');
      toast.success('Reply posted!');
    } catch (err) {
      console.error('Error posting reply:', err);
      toast.error(err.response?.data?.detail || 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  // Like/unlike comment
  const handleLike = async (commentId) => {
    if (!isAuthenticated) {
      toast.error('Please login to like comments');
      return;
    }

    setLikingComment(commentId);
    try {
      const response = await apiClient.post(`/comments/${commentId}/like`);
      const liked = response.data.liked;
      
      // Update comment likes
      setComments(comments.map(c => {
        if (c.id === commentId) {
          return {
            ...c,
            likes: c.likes + (liked ? 1 : -1),
            liked_by_me: liked
          };
        }
        // Also check replies
        if (c.replies) {
          return {
            ...c,
            replies: c.replies.map(r => {
              if (r.id === commentId) {
                return {
                  ...r,
                  likes: r.likes + (liked ? 1 : -1),
                  liked_by_me: liked
                };
              }
              return r;
            })
          };
        }
        return c;
      }));
    } catch (err) {
      console.error('Error liking comment:', err);
      toast.error('Failed to like comment');
    } finally {
      setLikingComment(null);
    }
  };

  // Delete comment
  const handleDelete = async () => {
    if (!deleteCommentId) return;

    try {
      await apiClient.delete(`/comments/${deleteCommentId}`);
      
      // Remove comment from list or mark as deleted
      setComments(comments.filter(c => c.id !== deleteCommentId).map(c => ({
        ...c,
        replies: c.replies?.filter(r => r.id !== deleteCommentId) || []
      })));
      
      toast.success('Comment deleted');
    } catch (err) {
      console.error('Error deleting comment:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete comment');
    } finally {
      setDeleteCommentId(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // CommentItem renderer — kept as helper (not nested component) so the
  // reply <Textarea> isn't unmounted on every keystroke. The previous
  // implementation defined CommentItem inside the parent, so each render
  // produced a new function reference → React unmounted/remounted the
  // textarea → focus was lost after the first character (COM-009).
  const renderCommentItem = (comment, isReply = false) => {
    const isOwner = user && user.id === comment.user_id;
    const isAdmin = user && ['admin', 'platform_admin', 'super_admin', 'reviewer', 'moderator'].includes(user.role);
    const canDelete = isOwner || isAdmin;
    const isDeleted = comment.is_deleted;

    return (
      <div key={comment.id} className={`${isReply ? 'ml-8 mt-3 pl-4 border-l-2 border-border' : ''}`}>
        <div className="flex gap-3">
          <Avatar className="w-8 h-8 flex-shrink-0">
            <AvatarImage src={comment.user_avatar} alt={comment.user_name} />
            <AvatarFallback className="text-xs">
              {comment.user_name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-foreground">
                {comment.user_name}
              </span>
              {comment.user_id === creatorId && (
                <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30">
                  Creator
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {formatDate(comment.created_at)}
              </span>
            </div>
            
            <p className={`text-sm mt-1 ${isDeleted ? 'text-muted-foreground italic' : 'text-foreground'}`}>
              {comment.content}
            </p>
            
            {/* Actions */}
            {!isDeleted && (
              <div className="flex items-center gap-4 mt-2">
                {/* Like */}
                <button
                  onClick={() => handleLike(comment.id)}
                  disabled={likingComment === comment.id}
                  className={`flex items-center gap-1 text-xs transition-colors ${
                    comment.liked_by_me 
                      ? 'text-red-500' 
                      : 'text-muted-foreground hover:text-red-500'
                  }`}
                  data-testid={`like-comment-${comment.id}`}
                >
                  {likingComment === comment.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Heart className={`w-3.5 h-3.5 ${comment.liked_by_me ? 'fill-current' : ''}`} />
                  )}
                  <span>{comment.likes || 0}</span>
                </button>
                
                {/* Reply (creator only, not on replies) */}
                {isCreator && !isReply && (
                  <button
                    onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`reply-comment-${comment.id}`}
                  >
                    <Reply className="w-3.5 h-3.5" />
                    <span>Reply</span>
                  </button>
                )}
                
                {/* Delete (owner or admin/moderator) */}
                {canDelete && (
                  <button
                    onClick={() => setDeleteCommentId(comment.id)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500 transition-colors"
                    data-testid={`delete-comment-${comment.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>{isOwner ? 'Delete' : 'Delete (Mod)'}</span>
                  </button>
                )}
              </div>
            )}
            
            {/* Reply form */}
            {replyingTo === comment.id && (
              <div className="mt-3 space-y-2">
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write your reply..."
                  className="min-h-[80px] text-sm"
                  maxLength={MAX_COMMENT_LENGTH}
                  data-testid="reply-textarea"
                  autoFocus
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {replyContent.length}/{MAX_COMMENT_LENGTH}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyContent('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleReply(comment.id)}
                      disabled={!replyContent.trim() || submitting}
                      data-testid="submit-reply-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5 mr-1" />
                          Reply
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-3 space-y-3">
            {comment.replies.map(reply => renderCommentItem(reply, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="bg-card border-border" data-testid="comments-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageCircle className="w-5 h-5" />
          Comments
          {comments.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {comments.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* New comment form */}
        {isAuthenticated ? (
          <form onSubmit={handleSubmitComment} className="space-y-3">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts or ask a question..."
              className="min-h-[100px]"
              maxLength={MAX_COMMENT_LENGTH}
              data-testid="new-comment-textarea"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {newComment.length}/{MAX_COMMENT_LENGTH}
              </span>
              <Button 
                type="submit" 
                disabled={!newComment.trim() || submitting}
                data-testid="submit-comment-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Post Comment
              </Button>
            </div>
          </form>
        ) : (
          <div className="text-center py-4 text-sm text-muted-foreground bg-muted/30 rounded-lg">
            <p>Please <a href="/login" className="text-primary hover:underline">log in</a> to leave a comment</p>
          </div>
        )}
        
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}
        
        {/* Error state */}
        {error && !loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-red-500">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <Button variant="ghost" size="sm" onClick={fetchComments}>
              Retry
            </Button>
          </div>
        )}
        
        {/* Empty state */}
        {!loading && !error && comments.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No comments yet. Be the first to share your thoughts!</p>
          </div>
        )}
        
        {/* Comments list */}
        {!loading && !error && comments.length > 0 && (
          <div className="space-y-4 divide-y divide-border">
            {comments.map(comment => (
              <div key={comment.id} className="pt-4 first:pt-0">
                {renderCommentItem(comment)}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      
      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteCommentId} onOpenChange={() => setDeleteCommentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default CampaignComments;
